import { useState, useRef } from "react";
import { Card, Button, ModalAlert, FormLoading } from '../../components'
import { UploadIcon, ChevronRightIcon, CheckCircleIcon, RefreshIcon, SaveIcon } from '@heroicons/react/solid'
import { DocumentTextIcon, TableIcon, ClipboardCheckIcon } from '@heroicons/react/outline'
import * as XLSX from 'xlsx'

// System fields for JOB01 datatype
const SYSTEM_FIELDS = [
  { key: "job_code",          label: "Job Code (รหัสงาน)",        required: true },
  { key: "cust_code",         label: "Customer Code (รหัสลูกค้า)", required: false },
  { key: "cust_name",         label: "Customer Name (ชื่อลูกค้า)", required: false },
  { key: "po_no",             label: "PO No.",                     required: false },
  { key: "product_code",      label: "Product Code (รหัสสินค้า)", required: false },
  { key: "product_name",      label: "Product Name (ชื่อสินค้า)", required: false },
  { key: "total_qty",         label: "Qty (จำนวน)",               required: true },
  { key: "unit_name",         label: "Unit (หน่วย)",              required: false },
  { key: "weight",            label: "Weight (น้ำหนัก kg)",       required: false },
  { key: "receiver_name",     label: "Receiver (ชื่อผู้รับ)",     required: true },
  { key: "address",           label: "Address (ที่อยู่)",          required: true },
  { key: "delivery_date",     label: "Delivery Date (วันที่จัดส่ง)", required: true },
  { key: "loc_receipt_name",  label: "Origin (ต้นทาง)",           required: false },
  { key: "loc_dest_name",     label: "Destination (ปลายทาง)",     required: false },
]

const STEPS = [
  { id: 1, label: "เลือกไฟล์", icon: UploadIcon },
  { id: 2, label: "Map Column", icon: TableIcon },
  { id: 3, label: "ตรวจสอบข้อมูล", icon: ClipboardCheckIcon },
  { id: 4, label: "นำเข้าสำเร็จ", icon: CheckCircleIcon },
]

const UploadCustomerFile = () => {
  const inputRef = useRef<any>(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState({ visible: false, tip: "" })
  const [fileName, setFileName] = useState("")
  const [customerColumns, setCustomerColumns] = useState<string[]>([])
  const [rawRows, setRawRows] = useState<any[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [previewData, setPreviewData] = useState<any[]>([])
  const [importSummary, setImportSummary] = useState({ total: 0 })
  const [optionAlert, setOptionAlert] = useState<any>({
    show: false, type: "success", title: "", action: { id: "", name: "" },
    description: { visible: false, type: "text", content: "", contentLists: [] }
  })

  // ── STEP 1: Read Excel ────────────────────────────────────────────
  const handleFileChange = (e: any) => {
    if (!e.target.files?.length) return
    const file = e.target.files[0]
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!["xls", "xlsx"].includes(ext || "")) {
      showAlert("danger", "ไฟล์ไม่ถูกต้อง", "กรุณาเลือกไฟล์ Excel (.xls, .xlsx) เท่านั้น")
      e.target.value = ""
      return
    }

    setLoading({ visible: true, tip: "กำลังอ่านไฟล์..." })
    setFileName(file.name)

    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target?.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: "array" })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const rows: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })

        if (rows.length < 2) {
          showAlert("danger", "ไฟล์ว่างเปล่า", "ไม่พบข้อมูลในไฟล์ กรุณาตรวจสอบอีกครั้ง")
          setLoading({ visible: false, tip: "" })
          return
        }

        const headers = (rows[0] as string[]).map(h => String(h).trim()).filter(h => h !== "")
        setCustomerColumns(headers)
        setRawRows(rows.slice(1, 6))

        // Auto-map exact matches (case-insensitive)
        const autoMap: Record<string, string> = {}
        SYSTEM_FIELDS.forEach(sf => {
          const match = headers.find(h => h.toLowerCase() === sf.key.toLowerCase())
          if (match) autoMap[sf.key] = match
        })
        setMapping(autoMap)

        setLoading({ visible: false, tip: "" })
        setCurrentStep(2)
      } catch {
        showAlert("danger", "อ่านไฟล์ไม่ได้", "ไม่สามารถอ่านไฟล์ได้ กรุณาตรวจสอบรูปแบบไฟล์")
        setLoading({ visible: false, tip: "" })
      }
    }
    reader.readAsArrayBuffer(file)
    e.target.value = ""
  }

  // ── STEP 2 → 3: Build preview from mapping ───────────────────────
  const buildPreview = () => {
    const requiredMapped = SYSTEM_FIELDS.filter(f => f.required).every(f => mapping[f.key])
    if (!requiredMapped) {
      showAlert("danger", "ข้อมูลไม่ครบ", "กรุณา map field ที่มีเครื่องหมาย * ให้ครบก่อน")
      return
    }

    const preview = rawRows.map((row, idx) => {
      const obj: any = { _row: idx + 2 }
      SYSTEM_FIELDS.forEach(sf => {
        const colName = mapping[sf.key]
        const colIdx = customerColumns.indexOf(colName)
        obj[sf.key] = colIdx >= 0 ? String(row[colIdx] ?? "").trim() : ""
      })
      return obj
    })
    setPreviewData(preview)
    setCurrentStep(3)
  }

  // ── STEP 3 → 4: Simulate import ──────────────────────────────────
  const confirmImport = () => {
    setLoading({ visible: true, tip: "กำลังนำเข้าข้อมูล..." })
    setTimeout(() => {
      setImportSummary({ total: rawRows.length })
      setLoading({ visible: false, tip: "" })
      setCurrentStep(4)
    }, 1200)
  }

  const reset = () => {
    setCurrentStep(1)
    setFileName("")
    setCustomerColumns([])
    setRawRows([])
    setMapping({})
    setPreviewData([])
    setImportSummary({ total: 0 })
  }

  const showAlert = (type: string, title: string, content: string) =>
    setOptionAlert({ ...optionAlert, show: true, type, title, description: { visible: true, type: "text", content, contentLists: [] } })

  // ── STEP INDICATOR ───────────────────────────────────────────────
  const StepBar = () => (
    <div className="flex items-center gap-0 mb-6">
      {STEPS.map((step, i) => {
        const done = currentStep > step.id
        const active = currentStep === step.id
        const Icon = step.icon
        return (
          <div key={step.id} className="flex items-center">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors
              ${done ? "bg-success-100 text-success-700" : active ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-400"}`}>
              <Icon className="w-4 h-4" />
              <span>{step.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRightIcon className={`w-4 h-4 mx-1 ${done ? "text-success-500" : "text-gray-300"}`} />
            )}
          </div>
        )
      })}
    </div>
  )

  // ── RENDER ───────────────────────────────────────────────────────
  return (
    <div className="space-y-3 p-3">
      <Card title="นำเข้าไฟล์ลูกค้า (Demo)">
        <FormLoading loading={loading.visible} tip={loading.tip}>
          <div className="p-2">
            <StepBar />

            {/* ── STEP 1: Upload ── */}
            {currentStep === 1 && (
              <div className="flex flex-col items-center justify-center min-h-[340px]">
                <div
                  className="w-full max-w-lg border-2 border-dashed border-gray-300 rounded-xl p-10 flex flex-col items-center gap-4
                    hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
                  onClick={() => inputRef.current?.click()}
                >
                  <DocumentTextIcon className="w-16 h-16 text-gray-300" />
                  <div className="text-center">
                    <p className="text-base font-medium text-gray-700">คลิกเพื่อเลือกไฟล์ Excel ของลูกค้า</p>
                    <p className="text-sm text-gray-400 mt-1">รองรับ .xls และ .xlsx</p>
                  </div>
                  <Button
                    text="เลือกไฟล์"
                    icon={<UploadIcon />}
                    iconPosition="start"
                    size="sm"
                    type="primary"
                    onClick={(e) => { e.stopPropagation(); inputRef.current?.click() }}
                  />
                </div>
                <p className="mt-4 text-xs text-gray-400">
                  ระบบจะอ่าน header row แรกของไฟล์ แล้วให้คุณ map column กับ field ของระบบ
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xls,.xlsx"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            )}

            {/* ── STEP 2: Column Mapping ── */}
            {currentStep === 2 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      ไฟล์: <span className="text-blue-600">{fileName}</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      พบ {customerColumns.length} column — เลือก column ที่ตรงกับ field ของระบบ
                      (<span className="text-danger-600">*</span> = จำเป็น)
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button text="ย้อนกลับ" size="sm" type="white" onClick={reset} />
                    <Button text="ถัดไป: ตรวจสอบ" size="sm" type="primary"
                      icon={<ChevronRightIcon />} iconPosition="end"
                      onClick={buildPreview} />
                  </div>
                </div>

                <div className="overflow-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-8">#</th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">Field ในระบบ</th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-16">จำเป็น</th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">Column ในไฟล์ลูกค้า</th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-20">สถานะ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {SYSTEM_FIELDS.map((sf, i) => (
                        <tr key={sf.key} className={mapping[sf.key] ? "bg-white" : sf.required ? "bg-red-50" : "bg-white"}>
                          <td className="px-4 py-2 text-gray-400 text-xs">{i + 1}</td>
                          <td className="px-4 py-2">
                            <span className="font-medium text-gray-800">{sf.label}</span>
                            <span className="ml-1 font-mono text-xs text-gray-400">({sf.key})</span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            {sf.required && <span className="text-danger-600 font-bold">*</span>}
                          </td>
                          <td className="px-4 py-2">
                            <select
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-400 focus:outline-none"
                              value={mapping[sf.key] || ""}
                              onChange={(e) => setMapping({ ...mapping, [sf.key]: e.target.value })}
                            >
                              <option value="">— ไม่ map —</option>
                              {customerColumns.map(col => (
                                <option key={col} value={col}>{col}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2 text-center">
                            {mapping[sf.key]
                              ? <CheckCircleIcon className="w-5 h-5 text-success-500 mx-auto" />
                              : sf.required
                                ? <span className="text-danger-500 text-xs">ยังไม่ map</span>
                                : <span className="text-gray-300 text-xs">—</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── STEP 3: Preview ── */}
            {currentStep === 3 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">ตรวจสอบข้อมูลก่อนนำเข้า</p>
                    <p className="text-xs text-gray-400 mt-0.5">แสดงตัวอย่าง {previewData.length} แถวแรก</p>
                  </div>
                  <div className="flex gap-2">
                    <Button text="แก้ไข Mapping" size="sm" type="white"
                      icon={<RefreshIcon />} iconPosition="start"
                      onClick={() => setCurrentStep(2)} />
                    <Button text="ยืนยันนำเข้า" size="sm" type="primary"
                      icon={<SaveIcon />} iconPosition="start"
                      onClick={() => setOptionAlert({
                        ...optionAlert, show: true, type: "confirm",
                        title: "ยืนยันการนำเข้า",
                        action: { id: "", name: "import" },
                        description: { visible: true, type: "text", content: "ต้องการนำเข้าข้อมูลทั้งหมดหรือไม่?", contentLists: [] }
                      })} />
                  </div>
                </div>

                <div className="overflow-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">แถว</th>
                        {SYSTEM_FIELDS.filter(sf => mapping[sf.key]).map(sf => (
                          <th key={sf.key} className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                            {sf.key}
                            <span className="block font-normal text-gray-400 normal-case">← {mapping[sf.key]}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previewData.map((row) => (
                        <tr key={row._row} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-400 text-xs">{row._row}</td>
                          {SYSTEM_FIELDS.filter(sf => mapping[sf.key]).map(sf => (
                            <td key={sf.key} className="px-3 py-2 text-gray-700 max-w-[180px] truncate">
                              {row[sf.key] || <span className="text-gray-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── STEP 4: Success ── */}
            {currentStep === 4 && (
              <div className="flex flex-col items-center justify-center min-h-[340px] text-center gap-6">
                <div className="w-20 h-20 rounded-full bg-success-100 flex items-center justify-center">
                  <CheckCircleIcon className="w-12 h-12 text-success-500" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">นำเข้าสำเร็จ!</h2>
                  <p className="text-gray-500 mt-2">
                    นำเข้าข้อมูลจาก <span className="font-medium text-gray-700">{fileName}</span> เรียบร้อยแล้ว
                  </p>
                  <div className="mt-4 inline-flex items-center gap-6 px-6 py-3 bg-gray-50 rounded-xl border">
                    <div>
                      <p className="text-2xl font-bold text-gray-800">{importSummary.total}</p>
                      <p className="text-xs text-gray-500">รายการทั้งหมด</p>
                    </div>
                    <div className="w-px h-10 bg-gray-200" />
                    <div>
                      <p className="text-2xl font-bold text-success-600">{importSummary.total}</p>
                      <p className="text-xs text-gray-500">นำเข้าสำเร็จ</p>
                    </div>
                    <div className="w-px h-10 bg-gray-200" />
                    <div>
                      <p className="text-2xl font-bold text-danger-500">0</p>
                      <p className="text-xs text-gray-500">ผิดพลาด</p>
                    </div>
                  </div>
                </div>
                <Button
                  text="นำเข้าไฟล์ใหม่"
                  icon={<RefreshIcon />}
                  iconPosition="start"
                  size="sm"
                  type="white"
                  onClick={reset}
                />
              </div>
            )}
          </div>
        </FormLoading>
      </Card>

      <ModalAlert
        {...optionAlert}
        onOk={() => {
          if (optionAlert.action?.name === "import") confirmImport()
          setOptionAlert({ ...optionAlert, show: false })
        }}
        onCancel={() => setOptionAlert({ ...optionAlert, show: false })}
      />
    </div>
  )
}

export default UploadCustomerFile
